'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { listDailyHoroscopes, DailyHoroscopeRecord, HoroscopeParsedContent } from '@/lib/api/client';
import { Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';

import { HoroscopePageContent } from '@/components/horoscope/HoroscopePageContent';

import { ZODIAC_METADATA } from '@/lib/config/zodiac';
import { formatUpdatedLabel, HoroscopeTabPeriod } from '@/lib/utils/horoscope-format';
import { Suspense } from 'react';

function TodayHoroscopeGridContent() {
  const searchParams = useSearchParams();
  const periodParam = searchParams.get('period') || searchParams.get('tab');
  
  const initialTab: HoroscopeTabPeriod = 
    periodParam && ['daily', 'weekly', 'monthly', 'yearly'].includes(periodParam)
      ? (periodParam as HoroscopeTabPeriod)
      : 'daily';

  const [activeTab, setActiveTab] = useState<HoroscopeTabPeriod>(initialTab);
  const [horoscopes, setHoroscopes] = useState<Record<string, DailyHoroscopeRecord>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (periodParam && ['daily', 'weekly', 'monthly', 'yearly'].includes(periodParam)) {
      setActiveTab(periodParam as HoroscopeTabPeriod);
    }
  }, [periodParam]);

  useEffect(() => {
    setLoading(true);
    listDailyHoroscopes(activeTab)
      .then((res) => {
        const map: Record<string, DailyHoroscopeRecord> = {};
        res.forEach((h) => {
          map[h.sign.toLowerCase()] = h;
        });
        setHoroscopes(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeTab]);

  const parseSummary = (record?: DailyHoroscopeRecord): string => {
    if (!record || record.isPending || !record.content) {
      return `Click to view and generate your ${activeTab} ${record?.sign || ''} forecast.`;
    }
    try {
      const parsed: HoroscopeParsedContent = typeof record.content === 'string' ? JSON.parse(record.content) : record.content;
      return parsed.summary || parsed.sections?.general || `Click to view ${activeTab} transit forecast.`;
    } catch {
      return record.content.slice(0, 100) + '...';
    }
  };

  const updatedLabel = formatUpdatedLabel(activeTab);

  return (
    <div className="space-y-8 max-w-7xl mx-auto py-6 px-4 sm:px-6">
      {/* Header Banner */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl relative overflow-hidden text-center sm:text-left">
        <div className="flex items-center gap-2 text-[#5ab4ff] text-xs font-semibold uppercase tracking-wider mb-2 justify-center sm:justify-start">
          <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
          <span>Vedic Transit Astrology</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-['Lexend'] font-medium text-white mb-2">
          Vedic Horoscope Forecasts
        </h1>
        <p className="text-[#dcecff]/75 text-xs sm:text-sm max-w-2xl leading-relaxed font-['Outfit']">
          Daily, Weekly, Monthly, and Yearly planetary transit insights per Moon Sign (Rashi) calculated deterministically with Swiss Ephemeris astronomical positions.
        </p>
      </div>

      {/* 4-Tab Navigation - Segmented Control (Today | Weekly | Monthly | Yearly) */}
      <div className="space-y-4">
        <div className="flex rounded-2xl bg-white/[0.04] p-1 border border-white/10 max-w-md">
          {[
            { id: 'daily', label: 'Today' },
            { id: 'weekly', label: 'Weekly' },
            { id: 'monthly', label: 'Monthly' },
            { id: 'yearly', label: 'Yearly' }
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as HoroscopeTabPeriod)}
                className={`flex-1 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-white text-[#050a24] shadow-[0_2px_12px_rgba(255,255,255,0.2)]'
                    : 'text-[#dcecff]/70 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Status Row: Dynamic Updated Label + Static Expert Verified Badge */}
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-[#dcecff]/70 bg-white/[0.03] px-5 py-3 rounded-2xl border border-white/10">
          <span className="font-mono text-white font-medium">{updatedLabel}</span>

          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8dffc9]/10 border border-[#8dffc9]/30 text-[#8dffc9] text-[11px] font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Expert Verified</span>
          </div>
        </div>
      </div>

      {/* 12 Zodiac Signs Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {ZODIAC_METADATA.map((z) => {
          const record = horoscopes[z.sign];
          const summaryText = parseSummary(record);
          const isPending = !record || record.isPending || !record.content;

          return (
            <Link
              key={z.sign}
              href={`/horoscope/today-horoscope/${z.sign}?period=${activeTab}`}
              className="group bg-[rgba(5,10,36,0.92)] border border-white/16 hover:border-[#5ab4ff]/50 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl transition-all hover:-translate-y-1 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl text-white">{z.symbol}</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/12 text-[#5ab4ff] font-medium">
                    {z.sanskrit}
                  </span>
                </div>

                <h3 className="text-xl font-['Lexend'] font-medium text-white group-hover:text-[#5ab4ff] transition-colors">
                  {z.name}
                </h3>
                <p className="text-xs text-[#dcecff]/60 mb-3 font-['Outfit']">{z.dates}</p>

                <p className={`text-xs line-clamp-3 leading-relaxed font-['Outfit'] ${isPending ? 'text-[#dcecff]/50 font-medium italic' : 'text-[#dcecff]/85'}`}>
                  {loading ? `Fetching ${activeTab} forecast...` : summaryText}
                </p>
              </div>

              <div className="mt-5 pt-3 border-t border-white/10 flex items-center justify-between text-xs text-[#5ab4ff] font-semibold group-hover:translate-x-1 transition-transform">
                <span className="capitalize">{isPending ? `Generate ${activeTab} Forecast` : `View ${activeTab} Forecast`}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Supporting Sections: Methodology, Persona Raj, FAQ, Cross-Links */}
      <HoroscopePageContent />
    </div>
  );
}

export default function TodayHoroscopeGridPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[#030712] py-12 text-center text-[#dcecff]/60">Loading forecast grid...</div>}>
      <TodayHoroscopeGridContent />
    </Suspense>
  );
}
